import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  ExpandLessIcon,
  ExpandMoreIcon,
  Collapse,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  BasicTable,
  TableHead
} from '@esgian/esgianui';
import { useTimezone } from '@hooks/useTimezone';
import { useTheme } from '@hooks/useTheme';
import { formatToDecimalPlaces } from '@helpers';
import { ProfileLink } from '@components/Links';

const InboundVesselInsightVesselTab = ({ vesselsLatestPosition, ports }) => {
  const { customScrollbar, theme } = useTheme();
  const { getTimeZoneDisplay } = useTimezone();
  const [openPort, setOpenPort] = useState({});

  const togglePort = (portName) => {
    setOpenPort((prev) => ({
      ...prev,
      [portName]: !prev[portName]
    }));
  };
  const groupedByPort = ports.reduce((grouped, singlePort) => {
    const vesselsForPort = vesselsLatestPosition.filter(
      (vessel) => vessel.destinationPortId === singlePort.id
    );

    grouped[singlePort.portName] = vesselsForPort;

    return grouped;
  }, {});
  const tableHeaders = ['Operators', 'Vessel name', 'ETA', 'Draught', 'Position received'];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    filter: false,
    search: false,
    elevation: 0,
    print: false,
    viewColumns: false,
    pagination: false,
    toolbar: false,
    rowsPerPageOptions: [10, 25, 50, 100]
  };

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {Object.entries(groupedByPort).map(([portName, vessels], indexTop) => (
        <Box key={portName} display="flex" flexDirection="column" gap={1.5} sx={{ mb: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => togglePort(portName)}
            sx={{ cursor: 'pointer' }}>
            <Box>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                Upcoming vessels to {portName} ({vessels[0]?.destinationCountryName})
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                {vessels?.length || 0} vessels
              </Typography>
            </Box>
            <IconButton size="small">
              {openPort[portName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={openPort[portName]} timeout="auto" unmountOnExit>
            <TableContainer sx={{ ...customScrollbar }} mode={theme.mode}>
              <BasicTable options={options}>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((name, index) => (
                      <TableCell key={`table-${indexTop}-${index}`} align="center" colSpan={1}>
                        <b>{name}</b>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vessels.map((vessel) => {
                    const draughtToDisplay = `${formatToDecimalPlaces(
                      vessel.draught,
                      1
                    )}/${formatToDecimalPlaces(vessel.vesselDraught, 1)}`;
                    const percentToDisplay = `${formatToDecimalPlaces(vessel.percentDraught, 1)}%`;
                    return (
                      <TableRow key={`${vessel.imo}-${indexTop}`}>
                        <TableCell align="center" colSpan={1}>
                          {vessel?.operatorId ? (
                            <ProfileLink
                              name={vessel?.operatorShortName}
                              profile={'operator'}
                              id={vessel?.operatorId}
                            />
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <ProfileLink
                            name={vessel.vesselName}
                            profile={'vessel'}
                            id={vessel.imo}
                          />
                        </TableCell>
                        <TableCell>{getTimeZoneDisplay(vessel.eta)}</TableCell>
                        <TableCell>
                          {draughtToDisplay} ({percentToDisplay})
                        </TableCell>
                        <TableCell>{getTimeZoneDisplay(vessel.timestamp)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </BasicTable>
            </TableContainer>
          </Collapse>
        </Box>
      ))}
    </Paper>
  );
};

InboundVesselInsightVesselTab.propTypes = {
  vesselsLatestPosition: PropTypes.array,
  ports: PropTypes.array
};

export default InboundVesselInsightVesselTab;
